/**
 * This file defines an asynchronous version of the localStorage API, backed by
 * an IndexedDB database.  It creates a global asyncStorage object that has
 * methods like the localStorage object.
 *
 * To store a value use setItem:
 *
 *   asyncStorage.setItem('key', 'value');
 *
 * If you want confirmation that the value has been stored, pass a callback
 * function as the third argument:
 *
 *  asyncStorage.setItem('key', 'newValue', function() {
 *    console.log('new value stored');
 *  });
 *
 * To read a value, call getItem(), but note that you must supply a callback
 * function that the value will be passed to asynchronously:
 *
 *  asyncStorage.getItem('key', function(value) {
 *    console.log('The value of key is:', value);
 *  });
 *
 * Note that unlike localStorage, asyncStorage does not allow you to store and
 * retrieve values by setting and querying properties directly. You cannot just
 * write asyncStorage.key; you have to explicitly call setItem() or getItem().
 *
 * removeItem(), clear(), length(), and key() are like the same-named methods of
 * localStorage, but, like getItem() and setItem() they take a callback
 * argument.
 *
 * The asynchronous nature of getItem() makes it tricky to retrieve multiple
 * values. But unlike localStorage, asyncStorage does not require the values you
 * store to be strings.  So if you need to save multiple values and want to
 * retrieve them together, in a single asynchronous operation, just group the
 * values into a single object. The properties of this object may not include
 * DOM elements, but they may include things like Blobs and typed arrays.
 *
 * Unit tests are in apps/gallery/test/unit/asyncStorage_test.js
 */

import type { IKVKeyType, IKVStorage } from '../interface/kvStorage';
import type { ILog } from '../interface/log';

/* eslint-disable-next-line max-lines-per-function */
function storage(
    databaseName = 'site-util-storage',
    storeName = 'keyvaluepairs',
    databaseVersion = 1,
    logger: ILog | null = null,
): IKVStorage {
    let database: IDBDatabase | null = null;

    function withDatabase(callback: () => void) {
        if (database) {
            callback();
        } else {
            const openRequest: IDBOpenDBRequest = indexedDB.open(databaseName, databaseVersion);
            openRequest.addEventListener('error', () => {
                logger?.error(
                    `asyncStorage: can't open database: ${openRequest.error?.name ?? ''}`,
                );
            });
            openRequest.onupgradeneeded = () => {
                /* First time setup: create an empty object store */
                openRequest.result.createObjectStore(storeName);
            };
            openRequest.onsuccess = () => {
                database = openRequest.result;
                callback();
            };
        }
    }

    function withStore(
        type: IDBTransactionMode,
        callback: (store: IDBObjectStore) => void,
        oncomplete: () => void,
    ) {
        withDatabase(() => {
            if (!database) {
                return;
            }
            const transaction = database.transaction(storeName, type);
            if (oncomplete) {
                transaction.oncomplete = oncomplete;
            }
            callback(transaction.objectStore(storeName));
        });
    }

    function getItem(key: IKVKeyType): Promise<unknown> {
        return new Promise((resolve, reject) => {
            let request: IDBRequest;
            withStore(
                'readonly',
                function getItemBody(store) {
                    request = store.get(key);
                    request.addEventListener('error', () => {
                        reject(
                            `Error in asyncStorage.getItem(): ${request.error?.name ?? 'unknown'}`,
                        );
                    });
                },
                function onComplete() {
                    const value = request.result || null;
                    resolve(value);
                },
            );
        });
    }

    function getBoolean(key: IKVKeyType): Promise<boolean> {
        return new Promise((resolve, reject) => {
            getItem(key)
                .then(value => {
                    resolve(Boolean(value));
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    function getString(key: IKVKeyType): Promise<string> {
        return new Promise((resolve, reject) => {
            getItem(key)
                .then(value => {
                    resolve(String(value));
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    function setItem(key: IKVKeyType, value: unknown): Promise<void> {
        return new Promise((resolve, reject) => {
            withStore(
                'readwrite',
                function setItemBody(store) {
                    const request = store.put(value, key);
                    request.addEventListener('error', () => {
                        reject(`Error in asyncStorage.setItem(): ${request.error?.name ?? ''}`);
                    });
                },
                resolve,
            );
        });
    }

    function removeItem(key: IKVKeyType): Promise<void> {
        return new Promise((resolve, reject) => {
            withStore(
                'readwrite',
                function removeItemBody(store) {
                    const request = store.delete(key);
                    request.addEventListener('error', () => {
                        reject(`Error in asyncStorage.removeItem(): ${request.error?.name ?? ''}`);
                    });
                },
                resolve,
            );
        });
    }

    function clear(): Promise<void> {
        return new Promise((resolve, reject) => {
            withStore(
                'readwrite',
                function clearBody(store) {
                    const request = store.clear();
                    request.addEventListener('error', () => {
                        reject(`Error in asyncStorage.clear(): ${request.error?.name ?? ''}`);
                    });
                },
                resolve,
            );
        });
    }

    function length(): Promise<number> {
        return new Promise((resolve, reject) => {
            let request: IDBRequest;
            withStore(
                'readonly',
                function lengthBody(store) {
                    request = store.count();
                    request.addEventListener('error', () => {
                        reject(`Error in asyncStorage.length(): ${request.error?.name ?? ''}`);
                    });
                },
                function onComplete() {
                    resolve(Number.parseInt(String(request.result), 10));
                },
            );
        });
    }

    return {
        getItem,
        getBoolean,
        getString,
        setItem,
        removeItem,
        clear,
        length,
    };
}

export default storage;
